/**
 * CRE Agent Mode bridge — the message channel between the CRE page (in Agent
 * Mode) and the Windows agent that hosts it. See docs/cre-agent-mode/BRIDGE.md.
 *
 * Transport: the agent's preload exposes `window.creAgentHost`:
 *   onCommand(listener) → unsubscribe     agent → CRE: { id, type, payload }
 *   emit(type, payload)                   CRE → agent: results and events
 * Every command gets exactly one `result` event: { id, ok, data } or
 * { id, ok: false, error: { code, message } }.
 *
 * This module is transport/dispatch only (pure, unit-tested); the CRE page
 * supplies the handlers (useCreAgentBridge).
 */

export const AGENT_BRIDGE_PROTOCOL_VERSION = 1;

/** agent → CRE */
export const AGENT_COMMANDS = Object.freeze({
  GET_STATE: "getState",
  SET_TEMPLATE: "setTemplate",
  SET_REPORT_LANGUAGE: "setReportLanguage",
  SET_ORGANISATION: "setOrganisation",
  PROCESS_AI: "processAi",
  GET_REPORT: "getReport",
  NEW_PATIENT: "newPatient",
  RELOAD_CATALOG: "reloadCatalog",
  STOP_EDITOR_MIC: "stopEditorMic",
  SET_DICTATION_LANGUAGE: "setDictationLanguage",
});

/** CRE → agent */
export const AGENT_EVENTS = Object.freeze({
  READY: "ready",
  STATE: "state",
  RESULT: "result",
  PROCESS_AI: "processAi", // { phase: "started" | "finished", ok? }
});

export class AgentBridgeError extends Error {
  constructor(code, message) {
    super(message || code);
    this.code = code;
  }
}

export function getAgentHost(win = typeof window !== "undefined" ? window : undefined) {
  const host = win?.creAgentHost;
  return host && typeof host.onCommand === "function" && typeof host.emit === "function"
    ? host
    : null;
}

/**
 * Connect handlers to a host.
 * @param {{ onCommand: Function, emit: Function }} host
 * @param {Record<string, (payload: any) => any>} handlers keyed by AGENT_COMMANDS values
 * @returns {{ emit: (type: string, payload?: any) => void, dispose: () => void }}
 */
export function createAgentBridge(host, handlers) {
  let disposed = false;

  const emit = (type, payload) => {
    if (disposed) return;
    try {
      host.emit(type, payload);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[agent-bridge] emit failed", type, err);
    }
  };

  const reply = (id, ok, dataOrError) =>
    emit(AGENT_EVENTS.RESULT, ok ? { id, ok: true, data: dataOrError } : { id, ok: false, error: dataOrError });

  const onCommand = async (message) => {
    const id = message?.id;
    const type = message?.type;
    if (id == null || typeof type !== "string") {
      reply(id ?? null, false, { code: "bad_command", message: "Command needs an id and a type" });
      return;
    }
    const handler = handlers[type];
    if (typeof handler !== "function") {
      reply(id, false, { code: "unknown_command", message: `Unknown command: ${type}` });
      return;
    }
    try {
      const data = await handler(message.payload ?? {});
      reply(id, true, data === undefined ? null : data);
    } catch (err) {
      reply(id, false, {
        code: err instanceof AgentBridgeError ? err.code : "failed",
        message: String(err?.message || err),
      });
    }
  };

  const unsubscribe = host.onCommand(onCommand);

  return {
    emit,
    dispose() {
      disposed = true;
      if (typeof unsubscribe === "function") unsubscribe();
    },
  };
}

/** Templates as the agent needs them (no report content). */
export function toAgentTemplates(templates) {
  return (Array.isArray(templates) ? templates : [])
    .filter((t) => t && t.templateId != null)
    .map((t) => ({
      templateId: String(t.templateId),
      templateName: String(t.templateName ?? ""),
      anathomy: t.anathomy ?? null,
    }));
}

/** Find a template by id (ids compared as strings). */
export function findTemplate(templates, templateId) {
  if (templateId == null) return null;
  return (Array.isArray(templates) ? templates : []).find(
    (t) => t && String(t.templateId) === String(templateId),
  ) || null;
}
